import { LocaleMessageType } from "@/types/locale";
import { useMemo } from "react";
import { useLocale } from "next-intl";
import { localizeFrom, useAnyTranslations } from "@lib/i18n/utils";
import { ActivityLanguage, ActivityTypeValue } from "@features/proposals/types/activity";
import { ProposalCustomStatus, ProposalWorkflowStatus } from "@features/proposals/types/proposal";

export const useTranslateProposalFields = () => {
  const t = useAnyTranslations("features.proposals.domain");
  const locale = useLocale();

  return useMemo(
    () => ({
      translateTypeValue: (typeValue?: ActivityTypeValue) =>
        t(`activity.fields.typeValue.values.${typeValue ?? "UNDECIDED"}.label`),
      translateTypeValueDescription: (typeValue?: ActivityTypeValue) =>
        t(`activity.fields.typeValue.values.${typeValue ?? "UNDECIDED"}.description`),
      translateWorkflowStatus: (status: ProposalWorkflowStatus) => ({
        label: t(`proposal.fields.workflow.status.values.${status}.label`),
        descriptionSpeaker: t(`proposal.fields.workflow.status.values.${status}.description`),
        descriptionTeam: t(`proposal.fields.workflow.status.values.${status}.descriptionTeam`),
      }),
      translateCustomStatus: (status: ProposalCustomStatus) => ({
        label: t(`proposal.fields.workflow.customStatus.values.${status}.label`),
      }),
      activity: {
        translateLanguage: (language: ActivityLanguage) =>
          t(`activity.fields.content.data.language.values.${language}`),
      },
      activityMember: {
        translateRole: (role: string) => t(`activityMember.fields.role.values.${role}.label`),
        translateRoleDescription: (role: string) =>
          t(`activityMember.fields.role.values.${role}.description`),
        translateParticipationFormat: (format: string) =>
          t(`activityMember.fields.participationFormat.values.${format}.label`),
        translateScope: (scope: string) => t(`activity.fields.scope.values.${scope}.label`),
        biography: {
          translateFieldLabel: (field: string) =>
            t(`activityMember.fields.biography.${field}.label`),
          translateFieldDescription: (field: string) =>
            t(`activityMember.fields.biography.${field}.description`),
          translateFieldPlaceholder: (field: string) =>
            t(`activityMember.fields.biography.${field}.placeholder`),
          translateFieldMetadata: (field: string, meta: string) =>
            t(`activityMember.fields.biography.${field}.${meta}`),
        },
      },
      localizeFrom: (from?: LocaleMessageType) => localizeFrom(from, locale),
    }),
    [locale, t],
  );
};
